import { MoreHorizontalIcon, PencilIcon, Trash2Icon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { deleteTradeFair, type TradeFair } from '@/api/tradeFairs'
import { useApiErrorMessage } from '@/api/errors'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

/**
 * Editing opens the dialog the page owns — one of it, not one per row. Deleting asks first: a fair
 * is not soft-deleted the way an account is, so once the leads exist there will be nothing to
 * restore, and today there is already no undo.
 */
export function TradeFairRowActions({
  fair,
  onEdit,
  onChanged,
}: {
  fair: TradeFair
  onEdit: () => void
  onChanged: () => void
}) {
  const { t } = useTranslation()
  const errorMessage = useApiErrorMessage()
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)

  async function remove() {
    if (busy) {
      return
    }
    setBusy(true)
    try {
      await deleteTradeFair(fair.id)
      toast.success(t('tradeFairs.delete.success', { name: fair.name }))
      onChanged()
    } catch (error) {
      // Once leads reference a fair the backend refuses this, and that refusal is the whole
      // message — a generic failure would leave an administrator retrying.
      toast.error(errorMessage(error, t('tradeFairs.delete.failed')))
    } finally {
      setBusy(false)
      setConfirming(false)
    }
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={t('tradeFairs.actions.menu', { name: fair.name })}
          >
            <MoreHorizontalIcon />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuItem onSelect={onEdit}>
            <PencilIcon />
            {t('tradeFairs.actions.edit')}
          </DropdownMenuItem>
          <DropdownMenuItem variant="destructive" onSelect={() => setConfirming(true)}>
            <Trash2Icon />
            {t('tradeFairs.actions.delete')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('tradeFairs.delete.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('tradeFairs.delete.description', { name: fair.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction disabled={busy} onClick={() => void remove()}>
              {t('tradeFairs.actions.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
